// ManiDine Restaurant App / src/RestaurantSignup.jsx / Updated: 2026-04-04 10:40 MDT
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./RestaurantSignup.css";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import { doc, GeoPoint, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import { geocodeAddress } from "./utils/geocodeAddress";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  restaurantName: "",
  brandName: "",
  legalBusinessName: "",
  contactTitle: "",
  supportEmail: "",
  supportPhone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  cuisineType: "",
  businessType: "",
  foodPermitNumber: "",
  businessLicenceNumber: "",
  gstNumber: "",
  acceptsTerms: false,
  acceptsMerchantAgreement: false,
  confirmsAuthorizedSigner: false,
};

const ACCOUNT_STEP_TITLES = ["Verify your email"];
const DETAILS_STEP_TITLES = [
  "Contact details",
  "Restaurant details",
  "Review & submit",
];

const RESTAURANT_SIGNUP_NOTICE_KEY = "manidine_restaurant_signup_notice";

const safeTrim = (v) => String(v ?? "").trim();
const digitsOnly = (v) => String(v ?? "").replace(/\D/g, "");

const stripHiddenChars = (s) =>
  String(s ?? "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/[\r\n\t]/g, " ");

const sanitizeByField = (name, raw) => {
  let v = stripHiddenChars(raw);
  v = String(v);

  if (name === "postalCode") {
    v = v.toUpperCase().replace(/[^A-Z0-9 ]/g, "");
    v = v.replace(/\s+/g, " ").trim();
    if (v.length > 7) v = v.slice(0, 7);
    return v;
  }

  if (name === "phone" || name === "supportPhone") {
    v = v.replace(/[^\d+\-() ]/g, "");
    if (v.length > 25) v = v.slice(0, 25);
    return v;
  }

  if (name === "email" || name === "supportEmail") {
    v = v.replace(/\s+/g, "").toLowerCase();
    if (v.length > 120) v = v.slice(0, 120);
    return v;
  }

  if (name === "foodPermitNumber" || name === "businessLicenceNumber" || name === "gstNumber") {
    v = v.toUpperCase().replace(/\s+/g, " ").trim();
    if (v.length > 40) v = v.slice(0, 40);
    return v;
  }

  if (
    name === "firstName" ||
    name === "lastName" ||
    name === "addressLine1" ||
    name === "addressLine2" ||
    name === "city" ||
    name === "restaurantName" ||
    name === "brandName" ||
    name === "legalBusinessName" ||
    name === "contactTitle" ||
    name === "cuisineType"
  ) {
    v = v.replace(/\s+/g, " ").trimStart();
    return v;
  }

  return v;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeTrim(value));
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

const isAllowedDocumentType = (file) => {
  const type = String(file?.type || "").toLowerCase();
  return type === "application/pdf" || type.startsWith("image/");
};

const sanitizeFileName = (name) =>
  String(name || "document")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_");

const saveSignupNotice = (notice) => {
  try {
    localStorage.setItem(RESTAURANT_SIGNUP_NOTICE_KEY, JSON.stringify(notice));
  } catch (err) {
    console.warn("[RestaurantSignup] failed to save signup notice:", err);
  }
};

const RestaurantSignup = ({ detailsOnly = false }) => {
  const [form, setForm] = useState(initialForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [foodPermitFile, setFoodPermitFile] = useState(null);
  const [businessLicenceFile, setBusinessLicenceFile] = useState(null);
  const navigate = useNavigate();
  const storage = useMemo(() => getStorage(), []);

  const stepTitles = detailsOnly ? DETAILS_STEP_TITLES : ACCOUNT_STEP_TITLES;
  const progressPercent = (currentStep / stepTitles.length) * 100;
  const accountEmail = safeTrim(form.email) || safeTrim(auth.currentUser?.email || "");

  useEffect(() => {
    if (!detailsOnly) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate("/login", { replace: true });
      return;
    }

    setForm((prev) => ({
      ...prev,
      email: safeTrim(currentUser.email || "").toLowerCase(),
    }));
  }, [detailsOnly, navigate]);

  const validateDocumentFile = (file, label) => {
    if (!file) return `${label} document is required.`;
    if (!isAllowedDocumentType(file)) {
      return `${label} must be a PDF or image file.`;
    }
    if (Number(file.size || 0) > MAX_UPLOAD_BYTES) {
      return `${label} must be under 15 MB.`;
    }
    return "";
  };

  const handleDocumentFileChange = (kind) => (e) => {
    const file = e.target?.files?.[0] || null;
    const label = kind === "foodPermit" ? "Food permit" : "Business licence";
    const error = file ? validateDocumentFile(file, label) : `${label} document is required.`;

    if (kind === "foodPermit") {
      setFoodPermitFile(error ? null : file);
    } else {
      setBusinessLicenceFile(error ? null : file);
    }

    setFieldErrors((prev) => {
      const next = { ...prev };
      const key = kind === "foodPermit" ? "foodPermitFile" : "businessLicenceFile";
      if (error) next[key] = error;
      else delete next[key];
      return next;
    });
  };

  const uploadRestaurantDocument = async ({ uid, docType, file }) => {
    const ext = String(file?.name || "").includes(".")
      ? String(file.name).split(".").pop()
      : "";
    const stamp = Date.now();
    const safeName = sanitizeFileName(
      ext ? `${docType}-${stamp}.${ext}` : `${docType}-${stamp}`
    );
    const fullPath = `restaurants/${uid}/documents/${docType}/${safeName}`;
    const fileRef = storageRef(storage, fullPath);

    await uploadBytes(fileRef, file, {
      contentType: file?.type || undefined,
    });

    const downloadUrl = await getDownloadURL(fileRef);

    return {
      documentName: file?.name || safeName,
      documentUrl: downloadUrl,
      storagePath: fullPath,
      contentType: file?.type || "",
      size: Number(file?.size || 0),
    };
  };

  const handleChange = (e) => {
    try {
      const { name, value, type, checked } = e.target;

      if (type === "checkbox") {
        setForm((prev) => ({ ...prev, [name]: !!checked }));
        return;
      }

      const sanitized = sanitizeByField(name, value);
      setForm((prev) => ({
        ...prev,
        [name]: sanitized,
      }));
    } catch (err) {
      console.error("[RestaurantSignup] handleChange error:", err);
      setGlobalError("Input error. Please try typing manually (no copy/paste).");
    }
  };

  const handlePaste = (name) => (e) => {
    try {
      const text = e.clipboardData?.getData("text") ?? "";
      const sanitized = sanitizeByField(name, text);
      e.preventDefault();
      setForm((prev) => ({ ...prev, [name]: sanitized }));
    } catch (err) {
      console.error("[RestaurantSignup] paste error:", err);
    }
  };

  const validateStep = (step) => {
    const errors = {};

    if (!detailsOnly) {
      const requiredFields = ["email", "password", "confirmPassword"];

      requiredFields.forEach((field) => {
        if (!safeTrim(form[field])) errors[field] = "This field is required.";
      });

      if (safeTrim(form.email) && !isValidEmail(form.email)) {
        errors.email = "Please enter a valid email.";
      }

      if (safeTrim(form.password).length < 6) {
        errors.password = "Password must be at least 6 characters.";
      }

      if (
        safeTrim(form.password) &&
        safeTrim(form.confirmPassword) &&
        safeTrim(form.password) !== safeTrim(form.confirmPassword)
      ) {
        errors.confirmPassword = "Passwords must match.";
      }

      return errors;
    }

    if (step === 1) {
      const requiredFields = ["firstName", "lastName", "phone"];
      requiredFields.forEach((field) => {
        if (!safeTrim(form[field])) errors[field] = "This field is required.";
      });

      if (digitsOnly(form.phone).length < 10) {
        errors.phone = "Please enter a valid phone number.";
      }
    } else if (step === 2) {
      const requiredFields = [
        "restaurantName",
        "legalBusinessName",
        "contactTitle",
        "supportEmail",
        "supportPhone",
        "addressLine1",
        "city",
        "postalCode",
        "cuisineType",
        "businessType",
        "foodPermitNumber",
        "businessLicenceNumber",
      ];

      requiredFields.forEach((field) => {
        if (!safeTrim(form[field])) errors[field] = "This field is required.";
      });

      if (safeTrim(form.supportEmail) && !isValidEmail(form.supportEmail)) {
        errors.supportEmail = "Please enter a valid support email.";
      }

      if (digitsOnly(form.supportPhone).length < 10) {
        errors.supportPhone = "Please enter a valid support phone.";
      }

      const foodPermitFileError = validateDocumentFile(foodPermitFile, "Food permit");
      if (foodPermitFileError) errors.foodPermitFile = foodPermitFileError;

      const businessLicenceFileError = validateDocumentFile(
        businessLicenceFile,
        "Business licence"
      );
      if (businessLicenceFileError) errors.businessLicenceFile = businessLicenceFileError;
    } else if (step === 3) {
      if (!form.acceptsTerms) errors.acceptsTerms = "You must agree before continuing.";
      if (!form.acceptsMerchantAgreement) {
        errors.acceptsMerchantAgreement = "You must accept the merchant agreement.";
      }
      if (!form.confirmsAuthorizedSigner) {
        errors.confirmsAuthorizedSigner = "You must confirm you are authorized to sign for this business.";
      }
    }

    return errors;
  };

  const isStep1Ready = useMemo(() => {
    if (!detailsOnly) {
      const requiredFields = ["email", "password", "confirmPassword"];
      const allFilled = requiredFields.every((field) => safeTrim(form[field]));
      const emailValid = isValidEmail(form.email);
      const passwordsMatch =
        safeTrim(form.password) && safeTrim(form.password) === safeTrim(form.confirmPassword);
      const passwordValid = safeTrim(form.password).length >= 6;
      return allFilled && emailValid && passwordsMatch && passwordValid;
    }

    const requiredFields = ["firstName", "lastName", "phone"];
    return (
      requiredFields.every((field) => safeTrim(form[field])) &&
      digitsOnly(form.phone).length >= 10
    );
  }, [detailsOnly, form]);

  const isStep2Ready = useMemo(() => {
    if (!detailsOnly) return true;

    const requiredFields = [
      "restaurantName",
      "legalBusinessName",
      "contactTitle",
      "supportEmail",
      "supportPhone",
      "addressLine1",
      "city",
      "postalCode",
      "cuisineType",
      "businessType",
      "foodPermitNumber",
      "businessLicenceNumber",
    ];

    return (
      requiredFields.every((field) => safeTrim(form[field])) &&
      isValidEmail(form.supportEmail) &&
      digitsOnly(form.supportPhone).length >= 10 &&
      !validateDocumentFile(foodPermitFile, "Food permit") &&
      !validateDocumentFile(businessLicenceFile, "Business licence")
    );
  }, [detailsOnly, form, foodPermitFile, businessLicenceFile]);

  const handleNext = () => {
    const errors = validateStep(currentStep);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setGlobalError("Please fix the highlighted fields.");
      return;
    }
    setGlobalError("");
    setCurrentStep((prev) => Math.min(prev + 1, stepTitles.length));
  };

  const handleBack = () => {
    setGlobalError("");
    setFieldErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleCreateAccount = async () => {
    const errors = validateStep(1);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setGlobalError("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    setGlobalError("");
    let createdUser = null;

    try {
      const email = safeTrim(form.email).toLowerCase();
      const cred = await createUserWithEmailAndPassword(
        auth,
        email,
        String(form.password ?? "")
      );
      createdUser = cred.user;

      await sendEmailVerification(cred.user, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });

      saveSignupNotice({
        email,
        type: "restaurant_signup_verify_email",
        sentAt: new Date().toISOString(),
      });

      await signOut(auth);
      setVerificationEmail(email);
      setSignupSuccess(true);
    } catch (err) {
      console.error("Account creation failed:", err);
      const isEmailInUse = err?.code === "auth/email-already-in-use";

      if (!isEmailInUse && createdUser) {
        try {
          await deleteUser(createdUser);
        } catch (deleteErr) {
          console.error("[RestaurantSignup] rollback deleteUser failed:", deleteErr);
        }
      }

      if (isEmailInUse) {
        setGlobalError("That email is already registered. Please log in or use a different email.");
      } else {
        setGlobalError(err?.message || "We couldn't create your account right now. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteSignup = async () => {
    const errors = validateStep(3);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setGlobalError("Please complete all required confirmations before submitting.");
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setGlobalError("Please sign in first to complete your restaurant onboarding.");
      return;
    }

    setSubmitting(true);
    setGlobalError("");

    try {
      await currentUser.reload();
      await currentUser.getIdToken(true);

      if (!currentUser.emailVerified) {
        setGlobalError("Please verify your email before completing restaurant onboarding.");
        await signOut(auth);
        navigate("/login", { replace: true });
        return;
      }

      const restaurantRef = doc(db, "restaurants", currentUser.uid);
      const existingRestaurant = await getDoc(restaurantRef);
      if (existingRestaurant.exists()) {
        navigate("/dashboard", { replace: true });
        return;
      }

      const firstName = safeTrim(form.firstName);
      const lastName = safeTrim(form.lastName);
      const contactName = [firstName, lastName].filter(Boolean).join(" ");

      const addressLine1 = safeTrim(form.addressLine1);
      const addressLine2 = safeTrim(form.addressLine2);
      const city = safeTrim(form.city);
      const postalCode = safeTrim(form.postalCode);

      const formattedAddress = [addressLine1, addressLine2, city, postalCode, "Canada"]
        .filter(Boolean)
        .join(", ");

      const fullAddress = `${addressLine1}, ${city}, ${postalCode}, Canada`.trim();
      let coords = null;
      try {
        coords = await geocodeAddress(fullAddress);
      } catch (geocodeErr) {
        console.warn("[RestaurantSignup] geocode failed, continuing without coordinates:", geocodeErr);
      }

      const foodPermitUpload = await uploadRestaurantDocument({
        uid: currentUser.uid,
        docType: "foodPermit",
        file: foodPermitFile,
      });
      const businessLicenceUpload = await uploadRestaurantDocument({
        uid: currentUser.uid,
        docType: "businessLicence",
        file: businessLicenceFile,
      });

      const payload = {
        uid: currentUser.uid,
        status: "pending_review",
        onboardingStatus: "pending_review",
        onboardingStage: "submitted_after_verified_email",
        active: true,
        isActive: true,
        isTestAccount: false,

        firstName,
        lastName,
        contactName,
        contactTitle: safeTrim(form.contactTitle),
        authorizedSignerName: contactName,
        authorizedSignerTitle: safeTrim(form.contactTitle),

        email: safeTrim(currentUser.email || accountEmail).toLowerCase(),
        phone: safeTrim(form.phone),
        supportEmail: safeTrim(form.supportEmail).toLowerCase(),
        supportPhone: safeTrim(form.supportPhone),
        supportContactsComplete: true,

        restaurantName: safeTrim(form.restaurantName),
        brandName: safeTrim(form.brandName),
        legalBusinessName: safeTrim(form.legalBusinessName),
        cuisineType: safeTrim(form.cuisineType),
        businessType: safeTrim(form.businessType),

        addressLine1,
        addressLine2,
        address: formattedAddress || addressLine1,
        city,
        postalCode,

        foodPermitNumber: safeTrim(form.foodPermitNumber),
        businessLicenceNumber: safeTrim(form.businessLicenceNumber),
        gstNumber: safeTrim(form.gstNumber),

        compliance: {
          foodPermitNumber: safeTrim(form.foodPermitNumber),
          businessLicenceNumber: safeTrim(form.businessLicenceNumber),
          gstNumber: safeTrim(form.gstNumber),
          merchantAgreementAccepted: true,
          authorizedSignerConfirmed: true,
          authorizedSignerName: contactName,
          authorizedSignerTitle: safeTrim(form.contactTitle),
          docsPendingVerification: true,
          documentsPendingUpload: false,
          emailVerifiedAt: serverTimestamp(),
        },

        documents: {
          foodPermit: {
            number: safeTrim(form.foodPermitNumber),
            uploaded: true,
            verified: false,
            documentName: foodPermitUpload.documentName,
            documentUrl: foodPermitUpload.documentUrl,
            storagePath: foodPermitUpload.storagePath,
            contentType: foodPermitUpload.contentType,
            size: foodPermitUpload.size,
            uploadedAt: serverTimestamp(),
            reviewedAt: null,
            reviewedBy: null,
            rejectionReason: "",
          },
          businessLicence: {
            number: safeTrim(form.businessLicenceNumber),
            uploaded: true,
            verified: false,
            documentName: businessLicenceUpload.documentName,
            documentUrl: businessLicenceUpload.documentUrl,
            storagePath: businessLicenceUpload.storagePath,
            contentType: businessLicenceUpload.contentType,
            size: businessLicenceUpload.size,
            uploadedAt: serverTimestamp(),
            reviewedAt: null,
            reviewedBy: null,
            rejectionReason: "",
          },
        },

        approvalChecklist: {
          stripeReady: false,
          menuReady: false,
          hoursComplete: false,
          taxConfigured: false,
          foodPermitUploaded: true,
          foodPermitVerified: false,
          businessLicenceUploaded: true,
          businessLicenceVerified: false,
          merchantAgreementAccepted: true,
          authorizedSignerConfirmed: true,
          supportContactsComplete: true,
          adminReviewed: false,
          approvedForOperations: false,
          emailVerified: true,
        },

        adminReview: {
          status: "pending_review",
          requiresApproval: true,
          readyForReview: true,
          canApprove: false,
          reviewedBy: null,
          reviewedAt: null,
          notes: "",
          pendingItems: [
            "stripe_onboarding",
            "food_permit_verification",
            "business_licence_verification",
            "hours_setup",
            "menu_setup",
            "tax_setup",
          ],
        },

        requiredBeforeApproval: [
          "stripe_onboarding",
          "food_permit_verification",
          "business_licence_verification",
          "hours_setup",
          "menu_setup",
          "tax_setup",
        ],

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        merchantAgreementAcceptedAt: serverTimestamp(),
        emailVerifiedAt: serverTimestamp(),
      };

      if (coords && typeof coords.lat === "number" && typeof coords.lng === "number") {
        payload.location = new GeoPoint(coords.lat, coords.lng);
        payload.lat = coords.lat;
        payload.lng = coords.lng;
      }

      await setDoc(restaurantRef, payload);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Restaurant onboarding failed:", err);
      setGlobalError(err?.message || "We couldn't finish your restaurant onboarding right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (label, name, type = "text", props = {}) => (
    <div className="rs-form-group" key={name}>
      <label htmlFor={name}>{label}</label>

      {type === "select" ? (
        <select
          id={name}
          name={name}
          value={form[name]}
          onChange={handleChange}
          disabled={submitting}
          {...props}
        >
          <option value="">Select an option</option>
          <option value="Restaurant">Restaurant</option>
          <option value="Ghost kitchen">Ghost kitchen</option>
          <option value="Grocery">Grocery</option>
          <option value="Retail">Retail</option>
          <option value="Bakery">Bakery</option>
          <option value="Cafe">Cafe</option>
          <option value="Shared kitchen">Shared kitchen</option>
        </select>
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={String(form[name] ?? "")}
          onChange={handleChange}
          disabled={submitting}
          onPaste={
            [
              "postalCode",
              "addressLine1",
              "addressLine2",
              "legalBusinessName",
              "foodPermitNumber",
              "businessLicenceNumber",
              "gstNumber",
            ].includes(name)
              ? handlePaste(name)
              : undefined
          }
          {...props}
        />
      )}

      {fieldErrors[name] && <p className="rs-field-error">{fieldErrors[name]}</p>}
    </div>
  );

  const renderUploadField = (label, name, file, onChange) => (
    <div className="rs-form-group rs-upload-group" key={name}>
      <label htmlFor={name}>{label}</label>
      <label className="rs-upload-box" htmlFor={name}>
        <span className="rs-upload-title">{file ? "Replace document" : "Upload document"}</span>
        <span className="rs-upload-subtitle">PDF, JPG, PNG up to 15 MB</span>
        <span className="rs-upload-file-name">{file ? file.name : "No file selected"}</span>
      </label>
      <input
        id={name}
        name={name}
        type="file"
        accept=".pdf,image/*"
        onChange={onChange}
        disabled={submitting}
        className="rs-upload-input"
      />
      {fieldErrors[name] && <p className="rs-field-error">{fieldErrors[name]}</p>}
    </div>
  );

  const renderAccountCreation = () => (
    <>
      <div
        style={{
          marginBottom: 18,
          borderRadius: 16,
          border: "1px solid rgba(15, 23, 42, 0.08)",
          background: "rgba(15, 23, 42, 0.03)",
          padding: 14,
        }}
      >
        <strong style={{ display: "block", marginBottom: 8 }}>Create your account first</strong>
        <div style={{ lineHeight: 1.6, color: "#475569" }}>
          We will send a verification email first. After you verify your email and sign in,
          you will complete the full restaurant onboarding, including business details and
          document uploads.
        </div>
      </div>

      {renderField("Email", "email", "email", {
        autoComplete: "email",
        inputMode: "email",
        placeholder: "you@example.com",
      })}
      <div className="rs-form-grid two-col">
        {renderField("Password", "password", "password")}
        {renderField("Confirm password", "confirmPassword", "password")}
      </div>

      <div className="rs-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleCreateAccount}
          disabled={!isStep1Ready || submitting}
        >
          {submitting ? "Sending verification…" : "Create account & verify email"}
        </button>
      </div>
    </>
  );

  const renderDetailsStep = () => {
    if (currentStep === 1) {
      return (
        <>
          <div className="rs-form-grid two-col">
            {renderField("First name", "firstName")}
            {renderField("Last name", "lastName")}
          </div>
          {renderField("Account email", "email", "email", {
            disabled: true,
            readOnly: true,
          })}
          {renderField("Mobile phone", "phone", "tel", {
            placeholder: "e.g. +1 780 555 5555",
            inputMode: "tel",
            autoComplete: "tel",
          })}
          <div className="rs-actions">
            <button
              type="button"
              className="primary-button"
              onClick={handleNext}
              disabled={!isStep1Ready || submitting}
            >
              Next
            </button>
          </div>
        </>
      );
    }

    if (currentStep === 2) {
      return (
        <>
          {renderField("Restaurant name", "restaurantName")}
          {renderField("Brand name (optional)", "brandName")}
          {renderField("Legal business name", "legalBusinessName")}
          <div className="rs-form-grid two-col">
            {renderField("Authorized signer title", "contactTitle", "text", {
              placeholder: "Owner, Director, Manager",
            })}
            {renderField("Business type", "businessType", "select")}
          </div>
          <div className="rs-form-grid two-col">
            {renderField("Support email", "supportEmail", "email", {
              autoComplete: "email",
              inputMode: "email",
            })}
            {renderField("Support phone", "supportPhone", "tel", {
              placeholder: "e.g. +1 780 555 0000",
              inputMode: "tel",
              autoComplete: "tel",
            })}
          </div>
          {renderField("Address line 1", "addressLine1")}
          {renderField("Address line 2 (optional)", "addressLine2")}
          <div className="rs-form-grid two-col">
            {renderField("City", "city")}
            {renderField("Postal code", "postalCode", "text", {
              placeholder: "e.g. T6L 7B9",
              autoComplete: "postal-code",
            })}
          </div>
          <div className="rs-form-grid two-col">
            {renderField("Cuisine type", "cuisineType")}
            {renderField("Food permit number", "foodPermitNumber", "text", {
              placeholder: "AHS permit number",
            })}
          </div>
          <div className="rs-form-grid two-col">
            {renderUploadField(
              "Food permit document",
              "foodPermitFile",
              foodPermitFile,
              handleDocumentFileChange("foodPermit")
            )}
            {renderField("GST/HST number (optional)", "gstNumber", "text", {
              placeholder: "Optional",
            })}
          </div>
          <div className="rs-form-grid two-col">
            {renderField("Business licence number", "businessLicenceNumber", "text", {
              placeholder: "Municipal business licence",
            })}
            {renderUploadField(
              "Business licence document",
              "businessLicenceFile",
              businessLicenceFile,
              handleDocumentFileChange("businessLicence")
            )}
          </div>

          <div className="rs-actions">
            <button
              type="button"
              className="ghost-button"
              onClick={handleBack}
              disabled={submitting}
            >
              Back
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={handleNext}
              disabled={!isStep2Ready || submitting}
            >
              Next
            </button>
          </div>
        </>
      );
    }

    const accountSummary = [
      { label: "First name", value: form.firstName },
      { label: "Last name", value: form.lastName },
      { label: "Email", value: accountEmail },
      { label: "Phone", value: form.phone },
    ];

    const restaurantSummary = [
      { label: "Restaurant name", value: form.restaurantName },
      { label: "Brand name", value: form.brandName || "—" },
      { label: "Legal business name", value: form.legalBusinessName },
      { label: "Authorized signer title", value: form.contactTitle },
      { label: "Support email", value: form.supportEmail },
      { label: "Support phone", value: form.supportPhone },
      { label: "Address line 1", value: form.addressLine1 },
      { label: "Address line 2", value: form.addressLine2 || "—" },
      { label: "City", value: form.city },
      { label: "Postal code", value: form.postalCode },
      { label: "Cuisine type", value: form.cuisineType },
      { label: "Business type", value: form.businessType },
      { label: "Food permit number", value: form.foodPermitNumber },
      { label: "Food permit document", value: foodPermitFile?.name || "—" },
      { label: "Business licence number", value: form.businessLicenceNumber },
      { label: "Business licence document", value: businessLicenceFile?.name || "—" },
      { label: "GST/HST number", value: form.gstNumber || "—" },
    ];

    return (
      <>
        <div
          style={{
            marginBottom: 18,
            borderRadius: 16,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            background: "rgba(15, 23, 42, 0.03)",
            padding: 14,
          }}
        >
          <strong style={{ display: "block", marginBottom: 8 }}>
            Approval requirements before ManiDine can activate this restaurant
          </strong>
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            <li>Stripe onboarding must be completed and payouts enabled.</li>
            <li>Food permit and business licence will be reviewed by ManiDine.</li>
            <li>Hours, tax setup, and menu setup must be completed after signup.</li>
          </ul>
        </div>

        <div className="rs-summary-grid">
          <div>
            <h4>Account</h4>
            <dl>
              {accountSummary.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <h4>Restaurant</h4>
            <dl>
              {restaurantSummary.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <label className="rs-terms">
          <input
            type="checkbox"
            name="confirmsAuthorizedSigner"
            checked={!!form.confirmsAuthorizedSigner}
            onChange={handleChange}
            disabled={submitting}
          />
          <span>I confirm I am authorized to sign and submit this business for onboarding.</span>
        </label>
        {fieldErrors.confirmsAuthorizedSigner && (
          <p className="rs-field-error">{fieldErrors.confirmsAuthorizedSigner}</p>
        )}

        <label className="rs-terms">
          <input
            type="checkbox"
            name="acceptsMerchantAgreement"
            checked={!!form.acceptsMerchantAgreement}
            onChange={handleChange}
            disabled={submitting}
          />
          <span>I agree to ManiDine’s merchant onboarding and compliance requirements.</span>
        </label>
        {fieldErrors.acceptsMerchantAgreement && (
          <p className="rs-field-error">{fieldErrors.acceptsMerchantAgreement}</p>
        )}

        <label className="rs-terms">
          <input
            type="checkbox"
            name="acceptsTerms"
            checked={!!form.acceptsTerms}
            onChange={handleChange}
            disabled={submitting}
          />
          <span>I agree to ManiDine’s Terms and Privacy Policy.</span>
        </label>
        {fieldErrors.acceptsTerms && <p className="rs-field-error">{fieldErrors.acceptsTerms}</p>}

        <div className="rs-actions">
          <button
            type="button"
            className="ghost-button"
            onClick={handleBack}
            disabled={submitting}
          >
            Back
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={handleCompleteSignup}
            disabled={
              !form.acceptsTerms ||
              !form.acceptsMerchantAgreement ||
              !form.confirmsAuthorizedSigner ||
              submitting
            }
          >
            {submitting ? "Submitting onboarding…" : "Submit restaurant onboarding"}
          </button>
        </div>
      </>
    );
  };

  const goToLogin = () => navigate("/login");

  if (signupSuccess && !detailsOnly) {
    return (
      <div className="rs-wrapper">
        <header className="rs-header">
          <div className="rs-header-left">ManiDine for Restaurants</div>
          <div className="rs-header-right">
            <button type="button">EN</button>
            <button type="button">Help</button>
            <button type="button" onClick={goToLogin}>
              Log in
            </button>
          </div>
        </header>

        <main className="rs-body">
          <section className="rs-hero">
            <div className="rs-hero-overlay">
              <h1>Verify your email to continue</h1>
              <p>
                We sent a verification email to {verificationEmail || safeTrim(form.email)}. Once
                you verify your email, sign in and we will take you to the full restaurant
                onboarding form.
              </p>
            </div>
          </section>

          <section className="rs-form-side">
            <div className="rs-card">
              <p className="rs-step-label">Step 1 of 1</p>
              <div className="rs-progress">
                <div className="rs-progress-bar" style={{ width: "100%" }} />
              </div>
              <h2 className="rs-card-title">Check your inbox</h2>
              <p className="rs-card-subtitle">
                After you click the verification link, return here and sign in to complete your
                restaurant details, document uploads, and final onboarding review.
              </p>

              <div
                style={{
                  borderRadius: 16,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  background: "rgba(15, 23, 42, 0.03)",
                  padding: 14,
                  marginBottom: 18,
                  lineHeight: 1.6,
                }}
              >
                <strong style={{ display: "block", marginBottom: 8 }}>Next steps</strong>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Open the verification email we just sent.</li>
                  <li>Click the verification link.</li>
                  <li>Come back and sign in.</li>
                  <li>Complete your restaurant onboarding after login.</li>
                </ol>
              </div>

              <div className="rs-actions" style={{ justifyContent: "space-between" }}>
                <button type="button" className="ghost-button" onClick={() => navigate("/signup")}>Back</button>
                <button type="button" className="primary-button" onClick={goToLogin}>
                  Go to login
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="rs-wrapper">
      <header className="rs-header">
        <div className="rs-header-left">ManiDine for Restaurants</div>
        <div className="rs-header-right">
          <button type="button">EN</button>
          <button type="button">Help</button>
          <button type="button" onClick={goToLogin}>
            Log in
          </button>
        </div>
      </header>

      <main className="rs-body">
        <section className="rs-hero">
          <div className="rs-hero-overlay">
            <h1>A new era of honest, transparent, commission-free food delivery</h1>
            <p>
              Join ManiDine to get commission-free orders from local customers and keep more of
              what you earn.
            </p>
          </div>
        </section>

        <section className="rs-form-side">
          <div className="rs-card">
            <p className="rs-step-label">
              Step {currentStep} of {stepTitles.length}
            </p>
            <div className="rs-progress">
              <div className="rs-progress-bar" style={{ width: `${progressPercent}%` }} />
            </div>
            <h2 className="rs-card-title">{detailsOnly ? "Complete your onboarding" : "Create your account"}</h2>
            <p className="rs-card-subtitle">
              {detailsOnly
                ? "Your email is verified. Now complete the full restaurant onboarding to submit your business for review."
                : "Create your restaurant account first. After email verification, you will continue with the full onboarding form."}
            </p>
            <p className="rs-card-step-title">{stepTitles[currentStep - 1]}</p>

            {globalError && <div className="rs-alert">{globalError}</div>}

            {!detailsOnly ? renderAccountCreation() : renderDetailsStep()}
          </div>
        </section>
      </main>
    </div>
  );
};

export default RestaurantSignup;
